// FP3-111B — Canonical viewer-preference resolver for Service Fidelity
// Monitoring. Every UI surface that renders fidelity-only content
// (comparisons, occurrence counters, over/under warnings, progress
// bars, percentages) must gate itself with `useFidelityPref().isOff`
// so a single flip of the account preference clears the entire app.
//
// Semantics:
//   • The preference is scoped to the CURRENT VIEWER (never to the
//     organization). A supervising provider with Fidelity Off sees
//     no fidelity UI even when their assistant has it On.
//   • Missing/undefined preference defaults to "on" (the prior
//     product behaviour) so upgrading users are never surprised.
//   • `isReady` is true once the auth bootstrap has finished, so
//     callers can avoid a flash of fidelity content on first paint.
//
// Kept intentionally trivial — the value comes from a single
// authoritative source (useAuth().preferences) and no ad-hoc caching
// or side-effects are performed here.
import { useAuth } from "@/lib/auth";

export function useFidelityPref() {
  const { user, preferences, loading } = useAuth();
  const raw = preferences?.fidelity_monitoring;
  const value = raw === "off" ? "off" : "on";
  return {
    value,
    isOff: value === "off",
    isOn: value === "on",
    // `isReady` guards against rendering fidelity chrome during the
    // auth bootstrap. Callers rendering fidelity UI should treat
    // `!isReady` the same as `isOff` — i.e. HIDE by default while the
    // preference has not yet arrived. Prevents a flash on first paint.
    isReady: !loading && !!user,
  };
}
